import React, { useState } from 'react';
import {
  Card, Form, Button, Alert, Spinner, Modal, Row, Col, InputGroup, Toast, Badge,
} from 'react-bootstrap';
import { Search, AlertCircle, Copy, CheckCircle2, ShieldCheck, User, MapPin } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';
import { buildFielFormData } from '../../utils/fielCrypto';
import { apiFetch, ensureOk } from '../../utils/apiError';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const InformacionFiscalTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isCiec = credentials.authMode === 'ciec';
  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const [requestId, setRequestId]         = useState('');
  const [loading, setLoading]             = useState(false);
  const [resultado, setResultado]         = useState(null);
  const [lastRequestId, setLastRequestId] = useState('');
  const [copied, setCopied]               = useState(false);
  const [errorMsg, setErrorMsg]           = useState('');
  const [showError, setShowError]         = useState(false);
  const [showToast, setShowToast]         = useState(false);
  const [lastRequest, setLastRequest]     = useState(null);

  const [vaultSaving, setVaultSaving]     = useState(false);
  const [vaultSaveOk, setVaultSaveOk]     = useState(false);
  const [vaultSaveErr, setVaultSaveErr]   = useState('');


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    setResultado(null);

    try {
      const token   = await getToken();
      const headers = { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc };
      if (isCiec) headers['Secret'] = credentials.secret;

      const params = new URLSearchParams();
      if (requestId.trim()) params.set('requestId', requestId.trim());

      const endpoint = isCiec ? 'informacionfiscal' : 'informacionfiscalfiel';
      const url      = `${API_URL_V2}/Consultar/${endpoint}${params.toString() ? `?${params}` : ''}`;

      let res;
      if (isCiec) {
        res = await apiFetch(url, { method: 'GET', headers });
      } else {
        const { formData, headers: fielHeaders } = await buildFielFormData(credentials, token);
        res = await apiFetch(url, { method: 'POST', headers: { ...headers, ...fielHeaders }, body: formData });
      }

      await ensureOk(res);

      const data = await res.json();
      if (!data.success) throw new Error(data.errorMessage || 'No se pudo obtener la información fiscal.');

      setResultado(data);
      if (data.requestId) {
        setLastRequestId(data.requestId);
        setRequestId(data.requestId);
      }
      setShowToast(true);

      setLastRequest({
        method:      isCiec ? 'GET' : 'POST',
        baseUrl:     `${API_URL_V2}/Consultar/${endpoint}`,
        queryParams: params.toString() || null,
        headers: {
          'Rfc': credentials.rfc,
          ...(isCiec ? { 'Secret': credentials.secret } : {}),
        },
        formFields: (isCiec || vaultRegistered) ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key',      varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name     || 'certificado.cer', varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
      });

      if (!isCiec && !vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) setVaultSaveOk(true);
        else setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener la información fiscal.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyRequestId = async () => {
    try { await navigator.clipboard.writeText(lastRequestId); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>
          <Form onSubmit={handleSubmit}>

            {/* ── Request ID ──────────────────────── */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label className="form-label-modern">
                  Request ID{' '}
                  <span className="text-muted fw-normal small">
                    (opcional — reutiliza una sesión SAT existente)
                  </span>
                </Form.Label>
                <InputGroup>
                  <Form.Control className="form-control-modern" type="text"
                    placeholder="Pega el requestId de una consulta anterior"
                    value={requestId} onChange={(e) => setRequestId(e.target.value)}
                    style={{ borderRadius: '10px 0 0 10px' }} />
                  {requestId && (
                    <Button variant="outline-secondary" onClick={() => setRequestId('')}
                      style={{ borderRadius: '0 10px 10px 0' }}>✕</Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {!isValid && (
              <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
                <AlertCircle size={16} className="me-2 flex-shrink-0" />
                <small>Completa las credenciales de arriba para habilitar la consulta.</small>
              </Alert>
            )}

            <Button type="submit" className="btn-gradient" disabled={!isValid || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Consultando…</>
                : <><Search size={18} className="me-2" />Consultar Información Fiscal</>
              }
            </Button>

          </Form>

          {/* ── Request ID banner ────────────────── */}
          {lastRequestId && (
            <div className="mt-3 p-3 d-flex align-items-center gap-3 flex-wrap"
              style={{ background: 'rgba(0,212,170,.07)',
                border: '1px solid rgba(0,212,170,.3)', borderRadius: 10 }}>
              <CheckCircle2 size={18} color="#00d4aa" className="flex-shrink-0" />
              <div className="flex-grow-1">
                <div className="text-muted small mb-1">
                  Request ID — se reutilizará automáticamente en la próxima consulta:
                </div>
                <code style={{ fontSize: '.85rem', color: '#1a1f3a',
                  wordBreak: 'break-all', background: 'transparent' }}>
                  {lastRequestId}
                </code>
              </div>
              <Button variant="outline-success" size="sm" onClick={copyRequestId}
                className="d-flex align-items-center gap-1 flex-shrink-0">
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          )}

          {/* ── Resultado ───────────────────────── */}
          {resultado && (
            <div className="mt-4">
              <ResultadoCard resultado={resultado} />
            </div>
          )}

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      {/* ── Toasts ─────────────────────────────── */}
      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">Información obtenida</strong>
        </Toast.Header>
        <Toast.Body>La información fiscal se consultó correctamente.</Toast.Body>
      </Toast>

      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      {/* ── Modal error ─────────────────────────── */}
      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al consultar información fiscal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorMsg}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Field = ({ label, value }) => (
  <Col xs={6} md={4}>
    <span className="text-muted d-block" style={{ fontSize: '.75rem' }}>{label}</span>
    <strong style={{ fontSize: '.85rem' }}>{value || '—'}</strong>
  </Col>
);

const ResultadoCard = ({ resultado }) => {
  const { datosIdentificacion: id, ubicacionFiscal: ub } = resultado;

  const situacionColor = id?.situacion?.toLowerCase().includes('activo') ? '#00d4aa' : '#ff6b6b';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Identificación */}
      {id && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: '1rem 1.25rem',
          border: '1px solid rgba(19,160,255,.2)',
        }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <User size={18} color="#13a0ff" />
            <strong style={{ color: '#1a1f3a' }}>Datos de Identificación</strong>
            {id.situacion && (
              <Badge className="ms-auto" style={{
                background: situacionColor, fontSize: '.75rem', fontWeight: 500,
              }}>
                {id.situacion}
              </Badge>
            )}
          </div>
          <Row className="g-2">
            <Field label="RFC"           value={id.rfc} />
            <Field label="Nombre"        value={id.nombre} />
            <Field label="CURP"          value={id.curp} />
            <Field label="Fecha nac."    value={id.fechaNacimiento} />
            <Field label="Situación"     value={id.detalleSituacion} />
            <Field label="Fecha situac." value={id.fechaSituacion} />
            <Field label="Fecha"         value={id.fecha} />
            <Field label="Hora"          value={id.hora} />
          </Row>
        </div>
      )}

      {/* Ubicación fiscal */}
      {ub && (
        <div style={{
          background: '#fff', borderRadius: 12, padding: '1rem 1.25rem',
          border: '1px solid rgba(0,212,170,.2)',
        }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <MapPin size={18} color="#00d4aa" />
            <strong style={{ color: '#1a1f3a' }}>Ubicación Fiscal</strong>
            {ub.estatusDomicilio && (
              <Badge className="ms-auto" bg="secondary" style={{ fontSize: '.75rem', fontWeight: 500 }}>
                {ub.estatusDomicilio}
              </Badge>
            )}
          </div>
          <Row className="g-2">
            <Field label="Entidad federativa"     value={ub.entidadFederativa} />
            <Field label="Municipio"              value={ub.municipioDemarcacionTerritorial} />
            <Field label="Localidad"              value={ub.localidad} />
            <Field label="Colonia"                value={ub.colonia} />
            <Field label="Tipo vialidad"          value={ub.tipoVialidad} />
            <Field label="Vialidad"               value={ub.nombreVialidad} />
            <Field label="Núm. exterior"          value={ub.numeroExterior} />
            <Field label="Núm. interior"          value={ub.numeroInterior} />
            <Field label="C.P."                   value={ub.codigoPostal} />
            <Field label="Entre calle"            value={ub.entreCalle} />
            <Field label="Y calle"                value={ub.yCalle} />
            <Field label="Tipo inmueble"          value={ub.tipoInmueble} />
            <Field label="AD"                     value={ub.ad} />
            <Field label="Alta domicilio"         value={ub.fechaAltaDomicilio} />
            <Field label="Estatus contribuyente"  value={ub.estatusContribuyenteEnDomicilio} />
            <Field label="Teléfono fijo"          value={ub.telefonoFijo} />
            <Field label="Teléfono móvil"         value={ub.telefonoMovil} />
            <Field label="Correo"                 value={ub.correo} />
            {ub.referencias && (
              <Col xs={12}>
                <span className="text-muted d-block" style={{ fontSize: '.75rem' }}>Referencias</span>
                <strong style={{ fontSize: '.85rem' }}>{ub.referencias}</strong>
              </Col>
            )}
          </Row>
        </div>
      )}
    </div>
  );
};

export default InformacionFiscalTab;
